/**
 * RBAC React Hooks
 *
 * Provides React hooks for permission checking and role-based access control.
 */

import type {
  UserRole,
  Permission,
  RoleAssignment,
  PermissionCheckResult,
  DataVisibilityRules,
  UserSubscription,
  PlanFeatures,
} from './types';
import {
  hasPermission,
  getHighestRole,
  requiresConsent,
  canAccessFeature,
  getParentVisibilityRules,
  getCounselorVisibilityRules,
  getOrgAdminVisibilityRules,
  FORBIDDEN_PERMISSIONS,
} from './permissions';

/**
 * User context for permission checking
 */
export interface RBACUser {
  id: string;
  roles: RoleAssignment[];
  subscription: UserSubscription | null;
  organizationId: string | null;
  cohortCode: string | null;
}

/**
 * Hook result type
 */
export interface UsePermissionsResult {
  // Role info
  roles: RoleAssignment[];
  highestRole: UserRole;
  isRole: (role: UserRole) => boolean;
  hasRole: (role: UserRole, contextType?: 'organization' | 'cohort', contextId?: string) => boolean;

  // Permission checking
  hasPermission: (permission: Permission) => PermissionCheckResult;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  canDo: (permission: Permission) => boolean;

  // Data visibility
  getVisibilityRules: (targetUserId: string) => DataVisibilityRules;

  // Feature access (subscription-based)
  canAccessFeature: (feature: keyof PlanFeatures) => boolean;
  subscription: UserSubscription | null;

  // Quick checks
  isStudent: boolean;
  isParent: boolean;
  isCounselor: boolean;
  isOrgAdmin: boolean;
  isSuperAdmin: boolean;
  isPrivileged: boolean;
}

/**
 * Create permission hooks for a user
 *
 * This is a factory function that creates hooks based on user data.
 * In a real app, this would be called from a context provider.
 */
export function createPermissionHooks(user: RBACUser | null): UsePermissionsResult {
  const roles = user?.roles ?? [];
  const subscription = user?.subscription ?? null;
  const highestRole = getHighestRole(roles);

  // Role checks
  const isRole = (role: UserRole): boolean => {
    return roles.some(r => r.role === role);
  };

  const hasRoleInContext = (
    role: UserRole,
    contextType?: 'organization' | 'cohort',
    contextId?: string
  ): boolean => {
    return roles.some(r => {
      if (r.role !== role) return false;
      if (!contextType) return true;
      if (r.contextType === 'global') return true;
      if (r.contextType !== contextType) return false;
      if (!contextId) return true;
      return r.contextId === contextId;
    });
  };

  // Permission checking
  const checkPermission = (permission: Permission): PermissionCheckResult => {
    if (!user) {
      return { allowed: false, reason: 'Not authenticated' };
    }

    // Check if permission requires consent
    if (requiresConsent(permission)) {
      return hasPermission(roles, permission, undefined);
    }

    return hasPermission(roles, permission);
  };

  const canDo = (permission: Permission): boolean => {
    return checkPermission(permission).allowed;
  };

  const hasAny = (permissions: Permission[]): boolean => {
    return permissions.some(p => canDo(p));
  };

  const hasAll = (permissions: Permission[]): boolean => {
    return permissions.every(p => canDo(p));
  };

  // Data visibility
  const getVisibility = (targetUserId: string): DataVisibilityRules => {
    // If viewing own data, full access (except for forbidden permissions)
    if (user?.id === targetUserId) {
      return {
        canViewProgress: true,
        canViewMood: true,
        canViewTechniques: true,
        canViewJournal: true,
        canViewAiChats: true,
        canViewCrisisFlags: true,
        isAggregate: false,
      };
    }

    // Parent viewing child
    if (isRole('parent')) {
      // In real implementation, this would fetch the family relationship
      // For now, return default parent visibility
      return getParentVisibilityRules(null);
    }

    // Counselor viewing student
    if (isRole('counselor')) {
      // In real implementation, check consent and cohort
      return getCounselorVisibilityRules(false, false);
    }

    // Org admin viewing member
    if (isRole('org_admin')) {
      return getOrgAdminVisibilityRules(true);
    }

    // Super admin
    if (isRole('super_admin')) {
      return {
        canViewProgress: true,
        canViewMood: true,
        canViewTechniques: true,
        canViewJournal: false,      // STILL false - privacy by design
        canViewAiChats: false,      // STILL false - privacy by design
        canViewCrisisFlags: true,
        isAggregate: false,
      };
    }

    // Default: no access to others' data
    return {
      canViewProgress: false,
      canViewMood: false,
      canViewTechniques: false,
      canViewJournal: false,
      canViewAiChats: false,
      canViewCrisisFlags: false,
      isAggregate: false,
    };
  };

  // Feature access
  const checkFeatureAccess = (feature: keyof PlanFeatures): boolean => {
    if (!subscription?.features) {
      // Demo/development mode: Grant Pro-level access to all features
      // In production, this would be restricted to free tier features
      const demoFeatures: Partial<PlanFeatures> = {
        journal: true,
        basic_techniques: true,
        full_technique_library: true,
        ai_coach: true,
        brain_gym: true,
        advanced_analytics: true,
        modules_limit: 999,
      };
      return demoFeatures[feature] === true || (feature === 'modules_limit' && true);
    }
    return canAccessFeature(feature, subscription.features);
  };

  // Quick role checks
  const isStudent = isRole('student') || isRole('managed_child');
  const isParent = isRole('parent');
  const isCounselor = isRole('counselor');
  const isOrgAdmin = isRole('org_admin');
  const isSuperAdmin = isRole('super_admin');
  const isPrivileged = isCounselor || isOrgAdmin || isSuperAdmin;

  return {
    roles,
    highestRole,
    isRole,
    hasRole: hasRoleInContext,
    hasPermission: checkPermission,
    hasAnyPermission: hasAny,
    hasAllPermissions: hasAll,
    canDo,
    getVisibilityRules: getVisibility,
    canAccessFeature: checkFeatureAccess,
    subscription,
    isStudent,
    isParent,
    isCounselor,
    isOrgAdmin,
    isSuperAdmin,
    isPrivileged,
  };
}

/**
 * Default hooks for unauthenticated users
 */
export const defaultPermissionHooks: UsePermissionsResult = createPermissionHooks(null);

/**
 * Check if a permission is forbidden (privacy by design)
 */
export function isForbiddenPermission(permission: Permission): boolean {
  return FORBIDDEN_PERMISSIONS.includes(permission);
}

/**
 * Get user-friendly description of a permission
 */
export function getPermissionDescription(permission: Permission): string {
  const descriptions: Record<Permission, string> = {
    'self:read': 'View your own profile and data',
    'self:write': 'Edit your own profile and data',
    'self:delete': 'Delete your account and data',
    'users:invite': 'Invite new users to the organization',
    'users:remove': 'Remove users from the organization',
    'users:view_list': 'View list of users',
    'users:edit_roles': 'Change user roles',
    'analytics:view_own': 'View your own analytics',
    'analytics:view_aggregate': 'View aggregate analytics',
    'analytics:view_individual': 'View individual user analytics',
    'analytics:export': 'Export analytics data',
    'content:view_own_progress': 'View your progress',
    'content:view_others_progress': 'View others\' progress',
    'content:view_own_journal': 'View your journal',
    'content:view_others_journal': 'View others\' journals (not permitted)',
    'content:view_own_ai_chats': 'View your AI coach conversations',
    'content:view_others_ai_chats': 'View others\' AI conversations (not permitted)',
    'content:view_own_mood': 'View your mood history',
    'content:view_others_mood': 'View others\' mood history',
    'interventions:view': 'View interventions',
    'interventions:create': 'Create new interventions',
    'interventions:assign': 'Assign interventions to counselors',
    'interventions:resolve': 'Resolve interventions',
    'billing:view': 'View billing information',
    'billing:manage': 'Manage billing and subscription',
    'org:view': 'View organization details',
    'org:edit_settings': 'Edit organization settings',
    'org:edit_branding': 'Customize organization branding',
    'org:view_audit_log': 'View audit log',
    'org:manage_cohorts': 'Manage cohorts',
    'platform:manage_tenants': 'Manage platform tenants',
    'platform:view_all_orgs': 'View all organizations',
    'platform:impersonate': 'Impersonate users for support',
  };

  return descriptions[permission] ?? permission;
}

/**
 * Get user-friendly name for a role
 */
export function getRoleDisplayName(role: UserRole): string {
  const names: Record<UserRole, string> = {
    student: 'Student',
    managed_child: 'Child (Managed)',
    parent: 'Parent/Guardian',
    counselor: 'Counselor',
    org_admin: 'Administrator',
    super_admin: 'Platform Admin',
  };

  return names[role] ?? role;
}

/**
 * Get description for a role
 */
export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    student: 'Primary app user with full access to personal features',
    managed_child: 'Student with parent oversight of progress',
    parent: 'Manages family account and views children\'s progress',
    counselor: 'Views consented student data and manages interventions',
    org_admin: 'Full organization management including users and billing',
    super_admin: 'Platform-wide administration access',
  };

  return descriptions[role] ?? '';
}
